import { FC, useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { Controller } from 'react-hook-form';
import { BaseForm } from '../utils/BaseForm';
import { ObjectFormProps, ObjectFormValues } from './interfaces/IObjectForm';
import { useObjectsTab, useAddReturns, useAddReturnsByRow } from '../items/hooks/HObjects';
import { useCompanies } from '../items/hooks/HCompanies';

interface ObjectFormExtendedProps extends ObjectFormProps {
    defaultValues?: Partial<ObjectFormValues>;
}

const toOptionalNumber = (value: any): number | undefined => {
    if (value === '' || value === null || value === undefined) return undefined;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
};

const ObjectForm: FC<ObjectFormExtendedProps> = ({ open, onClose, onSubmit, defaultValues }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [typeOptions, setTypeOptions] = useState<Record<string, any>[]>([]);
    const [zoningOptions, setZoningOptions] = useState<Record<string, any>[]>([]);
    const [occupancyOptions, setOccupancyOptions] = useState<Record<string, any>[]>([]);
    const [statusOptions, setStatusOptions] = useState<Record<string, any>[]>([]);
    const [selectedType, setSelectedType] = useState(String(defaultValues?.type_ ?? ''));

    const { companies } = useCompanies();
    const companyOptions = companies
        .map((c) => ({
            id: String(c.id ?? c.company_id ?? ''),
            name: String(c.company_name ?? c.company ?? c.name ?? '').trim(),
        }))
        .filter((c) => c.id && c.name);

    const { fetchAllTypes, fetchAllZonings, fetchAllOccupancies, fetchAllStatus   } = useObjectsTab();
    const { parsedData, loading: returnsLoading, error: returnsError, triggerFileDialog } = useAddReturns(defaultValues?.object_id ?? (defaultValues as any)?.id);
    const { addRow, loading: rowLoading, error: rowError, success: rowSuccess, clearStatus: clearRowStatus } = useAddReturnsByRow(defaultValues?.object_id ?? (defaultValues as any)?.id);
    const [rowDate, setRowDate] = useState('');
    const [rowReturn, setRowReturn] = useState('');
    const handleAddRow = async () => {
        const returnNum = parseFloat(rowReturn);
        if (!rowDate || isNaN(returnNum)) return;
        const ok = await addRow(rowDate, returnNum);
        if (ok) {
            setRowDate('');
            setRowReturn('');
        }
    };


    useEffect(() => {
        fetchAllTypes().then(setTypeOptions);
        fetchAllZonings().then(setZoningOptions);
        fetchAllOccupancies().then(setOccupancyOptions);
        fetchAllStatus().then(setStatusOptions);
    }, [fetchAllTypes, fetchAllZonings, fetchAllOccupancies, fetchAllStatus]);

    useEffect(() => {
        setSelectedType(String(defaultValues?.type_ ?? ''));
    }, [open, defaultValues?.type_]);

    const initialValues: ObjectFormValues = {
        object_id: defaultValues?.object_id,
        company_name: defaultValues?.company_name ?? '',
        name: defaultValues?.name ?? '',
        type_: defaultValues?.type_ ?? '',
        status: defaultValues?.status ?? '',
        owner: defaultValues?.owner,
        country: defaultValues?.country ?? '',
        state: defaultValues?.state ?? '',
        city: defaultValues?.city ?? '',
        location: defaultValues?.location ?? '',
        street: defaultValues?.street ?? '',
        address: defaultValues?.address ?? '',
        zip_code: defaultValues?.zip_code ?? '',
        zoning: defaultValues?.zoning ?? '',
        occupancy: defaultValues?.occupancy ?? '',
        currency: defaultValues?.currency ?? '',
        class: defaultValues?.class ?? '',
        units: defaultValues?.units,
        Floors: defaultValues?.Floors,
        Parking_Spaces: defaultValues?.Parking_Spaces,
        sqm: defaultValues?.sqm,
        GLA: defaultValues?.GLA,
        price: defaultValues?.price,
        yield_net: defaultValues?.yield_net,
        yield_gross: defaultValues?.yield_gross,
        gross_rental_income: defaultValues?.gross_rental_income,
        operating_expenses: defaultValues?.operating_expenses,
        NOI: defaultValues?.NOI,
        cap_rate: defaultValues?.cap_rate,
        yield_dev: defaultValues?.yield_dev,
        yield_rev: defaultValues?.yield_rev,
        img_url: defaultValues?.img_url ?? '',
        description: defaultValues?.description ?? '',
        nda: defaultValues?.nda ?? false,  
    };

    const transformData = (data: ObjectFormValues) => ({
        company_name: data.company_name?.trim() ?? '',
        name: data.name?.trim() ?? '',
        type_: data.type_?.trim() ?? '',
        status: data.status?.trim() ?? '',
        owner: toOptionalNumber(data.owner),
        country: data.country?.trim() ?? '',
        state: data.state?.trim() ?? '',
        city: data.city?.trim() ?? '',
        location: data.location?.trim() ?? '',
        street: data.street?.trim() ?? '',
        address: data.address?.trim() ?? '',
        zip_code: data.zip_code?.trim() ?? '',
        zoning: data.zoning?.trim() ?? '',
        occupancy: data.occupancy?.trim() ?? '',
        currency: data.currency?.trim() ?? '',
        class: data.class?.trim() ?? '',
        units: toOptionalNumber(data.units),
        Floors: toOptionalNumber(data.Floors),
        Parking_Spaces: toOptionalNumber(data.Parking_Spaces),
        sqm: toOptionalNumber(data.sqm),
        GLA: toOptionalNumber(data.GLA),
        price: toOptionalNumber(data.price),
        yield_net: toOptionalNumber(data.yield_net),
        yield_gross: toOptionalNumber(data.yield_gross),
        gross_rental_income: toOptionalNumber(data.gross_rental_income),
        operating_expenses: toOptionalNumber(data.operating_expenses),
        NOI: toOptionalNumber(data.NOI),
        cap_rate: toOptionalNumber(data.cap_rate),
        yield_dev: toOptionalNumber(data.yield_dev),
        yield_rev: toOptionalNumber(data.yield_rev),
        img_url: data.img_url?.trim() ?? '',
        description: data.description?.trim() ?? '',
        nda: Boolean(data.nda),
    });

    const handleSubmit = async (data: any) => {
        setIsSubmitting(true);
        try {
            await onSubmit(data);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <BaseForm
            key={open ? JSON.stringify(initialValues) : 'closed'}
            open={open}
            onClose={onClose}
            onSubmit={handleSubmit}
            title='Modify Object'
            defaultValues={initialValues}
            transformData={transformData}
            loading={isSubmitting}
            dialogProps={{
                maxWidth: false,
                fullWidth: true,
                PaperProps: {
                    sx: {
                        width: { xs: '95vw', md: '75vw' },
                    },
                },
            }}
        >
            {(control) => (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 2fr' },
                        gap: 3,
                    }}
                >
                    <Box>
                        <Controller
                            name='name'
                            control={control}
                            rules={{ required: 'Name is required' }}
                            render={({ field }) => <TextField {...field} label='Name' variant='outlined' fullWidth margin='normal' />}
                        />
                        <Controller
                            name='type_'
                            control={control}
                            rules={{ required: 'Type is required' }}
                            render={({ field }) => (
                                <FormControl variant='outlined' fullWidth margin='normal'>
                                    <InputLabel>Type *</InputLabel>
                                    <Select
                                        {...field}
                                        label='Type *'
                                        value={field.value ?? ''}
                                        onChange={(event) => {
                                            field.onChange(event);
                                            setSelectedType(String(event.target.value ?? ''));
                                        }}
                                    >
                                        <MenuItem value=''><em>None</em></MenuItem>
                                        {typeOptions.map((opt, i) => (
                                            <MenuItem key={i} value={opt.description ?? ''}>{opt.description ?? ''}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                        <Controller
                            name='status'
                            control={control}
                            render={({ field }) => (
                                <FormControl variant='outlined' fullWidth margin='normal'>
                                    <InputLabel>Status</InputLabel>
                                    <Select {...field} label='Status' value={field.value ?? ''}>
                                        <MenuItem value=''><em>None</em></MenuItem>
                                        {statusOptions.map((opt, i) => (
                                            <MenuItem key={i} value={opt.description ?? ''}>{opt.description ?? ''}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                        <Controller
                            name='owner'
                            control={control}
                            render={({ field }) => (
                                <FormControl variant='outlined' fullWidth margin='normal'>
                                    <InputLabel>Owner</InputLabel>
                                    <Select
                                        label='Owner'
                                        value={field.value !== undefined && field.value !== null ? String(field.value) : ''}
                                        onChange={(e) => {
                                            const val = (e.target as HTMLInputElement).value;
                                            field.onChange(val === '' ? undefined : Number(val));
                                        }}
                                        inputRef={field.ref}
                                    >
                                        <MenuItem value=''><em>None</em></MenuItem>
                                        {companyOptions.map((opt, i) => (
                                            <MenuItem key={i} value={opt.id ?? ''}>{opt.name ?? ''}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                        <Controller
                            name='country'
                            control={control}
                            rules={{ required: 'Country is required' }}
                            render={({ field }) => <TextField {...field} label='Country *' variant='outlined' fullWidth margin='normal' />}
                        />
                        <Controller
                            name='state'
                            control={control}
                            render={({ field }) => <TextField {...field} label='State' variant='outlined' fullWidth margin='normal' />}
                        />
                        <Controller
                            name='city'
                            control={control}
                            render={({ field }) => <TextField {...field} label='City' variant='outlined' fullWidth margin='normal' />}
                        />
                        <Controller
                            name='location'
                            control={control}
                            render={({ field }) => <TextField {...field} label='Location' variant='outlined' fullWidth margin='normal' />}
                        />
                        <Controller
                            name='street'
                            control={control}
                            render={({ field }) => <TextField {...field} label='Street' variant='outlined' fullWidth margin='normal' />}
                        />
                        <Controller
                            name='address'
                            control={control}
                            render={({ field }) => <TextField {...field} label='Address' variant='outlined' fullWidth margin='normal' />}
                        />
                        <Controller
                            name='zip_code'
                            control={control}
                            rules={{ required: 'Zip Code is required' }}
                            render={({ field }) => <TextField {...field} label='Zip Code *' variant='outlined' fullWidth margin='normal' />}
                        />
                        <Controller
                            name='currency'
                            control={control}
                            render={({ field }) => <TextField {...field} label='Currency' variant='outlined' fullWidth margin='normal' />}
                        />
                        <Controller
                            name='class'
                            control={control}
                            render={({ field }) => <TextField {...field} label='Class' variant='outlined' fullWidth margin='normal' />}
                        />
                        <Controller
                            name='price'
                            control={control}
                            render={({ field }) => <TextField {...field} type='number' label='Price' variant='outlined' fullWidth margin='normal' />}
                        />
                        
                        <Controller
                            name='gross_rental_income'
                            control={control}
                            render={({ field }) => <TextField {...field} type='number' label='Gross Rental Income' variant='outlined' fullWidth margin='normal' />}
                        />
                        <Controller
                            name='operating_expenses'
                            control={control}
                            render={({ field }) => <TextField {...field} type='number' label='Operating Expenses' variant='outlined' fullWidth margin='normal' />}
                        />
                        <Controller
                            name='cap_rate'
                            control={control}
                            render={({ field }) => <TextField {...field} type='number' label='Cap Rate' variant='outlined' fullWidth margin='normal' />}
                        />
                        
                        <Controller
                            name='img_url'
                            control={control}
                            render={({ field }) => <TextField {...field} label='Image URL' variant='outlined' fullWidth margin='normal' />}
                        />
                       

                    </Box>

                    <Box
                        sx={{
                            border: '1px solid #d9d9d9',
                            borderRadius: 1,
                            p: 2,
                            position: 'relative',
                        }}
                    >
                        <Controller
                            name='nda'
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={(
                                        <Switch
                                            name={field.name}
                                            inputRef={field.ref}
                                            checked={Boolean(field.value)}
                                            onChange={(e, checked) => field.onChange(checked)}
                                            color='primary'
                                        />
                                    )}
                                    label='NDA'
                                />
                            )}
                        />

                        <Controller
                            name='company_name'
                            control={control}
                            rules={{ required: 'Company is required' }}
                            render={({ field }) => (
                                <FormControl variant='outlined' fullWidth margin='normal'>
                                    <InputLabel>Company Name *</InputLabel>
                                    <Select {...field} label='Company Name *' value={field.value ?? ''}>
                                        <MenuItem value=''><em>None</em></MenuItem>
                                        {companyOptions.map((opt, i) => (
                                            <MenuItem key={i} value={opt.name ?? ''}>{opt.name ?? ''}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />

                        <Controller
                            name='occupancy'
                            control={control}
                            render={({ field }) => (
                                <FormControl variant='outlined' fullWidth margin='normal'>
                                    <InputLabel>Occupancy</InputLabel>
                                    <Select {...field} label='Occupancy' value={field.value ?? ''}>
                                        <MenuItem value=''><em>None</em></MenuItem>
                                        {occupancyOptions.map((opt, i) => (
                                            <MenuItem key={i} value={opt.description ?? ''}>{opt.description ?? ''}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                        <Controller
                            name='units'
                            control={control}
                            render={({ field }) => <TextField {...field} type='number' label='units' variant='outlined' fullWidth margin='normal' />}
                        />
                        <Controller
                            name='Floors'
                            control={control}
                            render={({ field }) => <TextField {...field} type='number' label='Floors' variant='outlined' fullWidth margin='normal' />}
                        />
                        <Controller
                            name='zoning'
                            control={control}
                            render={({ field }) => (
                                <FormControl variant='outlined' fullWidth margin='normal'>
                                    <InputLabel>Zoning</InputLabel>
                                    <Select {...field} label='Zoning' value={field.value ?? ''}>
                                        <MenuItem value=''><em>None</em></MenuItem>
                                        {zoningOptions.map((opt, i) => (
                                            <MenuItem key={i} value={opt.description ?? ''}>{opt.description ?? ''}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                        <Controller
                            name='NOI'
                            control={control}
                            render={({ field }) => <TextField {...field} type='number' label='NOI' variant='outlined' fullWidth margin='normal' />}
                        />
                        <Controller
                            name='Parking_Spaces'
                            control={control}
                            render={({ field }) => <TextField {...field} type='number' label='Parking Spaces' variant='outlined' fullWidth margin='normal' />}
                        />
                        <Controller
                            name='sqm'
                            control={control}
                            render={({ field }) => <TextField {...field} type='number' label='SQM' variant='outlined' fullWidth margin='normal' />}
                        />
                        <Controller
                            name='GLA'
                            control={control}
                            render={({ field }) => <TextField {...field} type='number' label='GLA' variant='outlined' fullWidth margin='normal' />}
                        />
                        <Controller
                            name='yield_dev'
                            control={control}
                            render={({ field }) => <TextField {...field} type='number' label='Yield Dev' variant='outlined' fullWidth margin='normal' />}
                        />
                        <Controller
                            name='yield_rev'
                            control={control}
                            render={({ field }) => <TextField {...field} type='number' label='Yield Rev' variant='outlined' fullWidth margin='normal' />}
                        />
                        <Controller
                            name='yield_net'
                            control={control}
                            render={({ field }) => <TextField {...field} type='number' label='Yield Net' variant='outlined' fullWidth margin='normal' />}
                        />
                        <Controller
                            name='yield_gross'
                            control={control}
                            render={({ field }) => <TextField {...field} type='number' label='Yield Gross' variant='outlined' fullWidth margin='normal' />}
                        />
                    </Box>

                    <Box>
                        <Controller
                            name='description'
                            control={control}
                            render={({ field }) => (
                                <TextField {...field} label='Description' variant='outlined' fullWidth margin='normal' multiline minRows={18} />
                            )}
                        />
                        {selectedType.trim().toLowerCase() === 'fund' && (
                            <Box>
                                <Button
                                    type='button'
                                    variant='contained'
                                    color='primary'
                                    sx={{ mt: 1 }}
                                    onClick={triggerFileDialog}
                                    disabled={returnsLoading}
                                >
                                    {returnsLoading ? 'Processing...' : 'Add Returns'}
                                </Button>
                                {parsedData && (
                                    <Alert severity='success' sx={{ mt: 1 }}>
                                        ✓ {parsedData.length} monthly returns loaded successfully
                                    </Alert>
                                )}
                                {returnsError && (
                                    <Alert severity='error' sx={{ mt: 1 }}>
                                        {returnsError}
                                    </Alert>
                                )}
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant='subtitle2' sx={{ mb: 1 }}>Add Single Return</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <TextField
                                            label='Date'
                                            type='date'
                                            size='small'
                                            value={rowDate}
                                            onChange={(e) => { setRowDate(e.target.value); clearRowStatus(); }}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ flex: 1 }}
                                        />
                                        <TextField
                                            label='Return'
                                            type='number'
                                            size='small'
                                            value={rowReturn}
                                            onChange={(e) => { setRowReturn(e.target.value); clearRowStatus(); }}
                                            sx={{ flex: 1 }}
                                        />
                                        <Button
                                            type='button'
                                            variant='outlined'
                                            size='small'
                                            onClick={handleAddRow}
                                            disabled={rowLoading || !rowDate || rowReturn === ''}
                                            sx={{ height: 40 }}
                                        >
                                            {rowLoading ? '...' : 'Add'}
                                        </Button>
                                    </Box>
                                    {rowSuccess && (
                                        <Alert severity='success' sx={{ mt: 1 }}>Row added successfully</Alert>
                                    )}
                                    {rowError && (
                                        <Alert severity='error' sx={{ mt: 1 }}>{rowError}</Alert>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            )}
        </BaseForm>
    );
};

export default ObjectForm;
